import { UzButton, UzCheckbox, UzInput } from '@utazzo/ui';
import React, { useEffect, useState } from 'react';

import { useScriptsConfig, useUpdateScriptsConfig } from './use-scripts';

import type { AdminWebsiteScriptsRequest, WebsiteScriptsConfig } from '@utazzo/shared';

type FieldKey = Exclude<keyof WebsiteScriptsConfig, 'meta_capi_enabled' | 'meta_capi_lead_enabled'>;

const Field = ({
  label,
  htmlFor,
  hint,
  children,
}: {
  label: string;
  htmlFor: string;
  hint?: string;
  children: React.ReactNode;
}): React.JSX.Element => (
  <div>
    <label htmlFor={htmlFor} className="mb-1 block text-xs font-medium text-(--fg-muted)">
      {label}
    </label>
    {children}
    {hint && <p className="mt-1 text-[12px] text-(--fg-faint)">{hint}</p>}
  </div>
);

/** Normalize an `<input>` / `<textarea>` string to either a non-empty trimmed string or `null`. */
const normalise = (v: string): string | null => {
  const t = v.trim();
  return t.length === 0 ? null : t;
};

const FIELD_KEYS: FieldKey[] = [
  'gtm_id',
  'ga4_id',
  'meta_pixel_id',
  'instaform_pixel_id',
  'clarity_id',
  'customerlabs_id',
];

export const ScriptsTagsTab = ({ canWrite = true }: { canWrite?: boolean }): React.JSX.Element => {
  const { data, isLoading } = useScriptsConfig();
  const save = useUpdateScriptsConfig();

  const [form, setForm] = useState<WebsiteScriptsConfig>({
    gtm_id: null,
    ga4_id: null,
    meta_pixel_id: null,
    instaform_pixel_id: null,
    clarity_id: null,
    customerlabs_id: null,
    meta_capi_enabled: true,
    meta_capi_lead_enabled: true,
  });

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  if (isLoading || !data) return <p className="p-6 text-sm text-(--fg-faint)">Loading…</p>;

  const update = (key: FieldKey, raw: string): void => {
    setForm((prev) => ({ ...prev, [key]: normalise(raw) }));
  };

  const onSave = async (): Promise<void> => {
    const entries: AdminWebsiteScriptsRequest['entries'] = FIELD_KEYS.filter(
      (k) => form[k] !== data[k],
    ).map((k) => ({ key: `scripts.${k}`, value: form[k] }));
    if (form.meta_capi_enabled !== data.meta_capi_enabled) {
      entries.push({
        key: 'scripts.meta_capi_enabled',
        value: form.meta_capi_enabled ? 'true' : 'false',
      });
    }
    if (form.meta_capi_lead_enabled !== data.meta_capi_lead_enabled) {
      entries.push({
        key: 'scripts.meta_capi_lead_enabled',
        value: form.meta_capi_lead_enabled ? 'true' : 'false',
      });
    }
    if (entries.length === 0) return;
    await save.mutateAsync({ entries });
  };

  const dirty =
    FIELD_KEYS.some((k) => form[k] !== data[k]) ||
    form.meta_capi_enabled !== data.meta_capi_enabled ||
    form.meta_capi_lead_enabled !== data.meta_capi_lead_enabled;

  return (
    <div className="flex max-w-3xl flex-col gap-6 p-6">
      <section className="flex flex-col gap-4">
        <h2 className="text-sm font-semibold text-(--fg)">Tag IDs</h2>
        <Field
          label="GTM container ID"
          htmlFor="scripts-gtm-id"
          hint='Find in Google Tag Manager → Workspace → top-right (looks like "GTM-XXXXXX").'
        >
          <UzInput
            id="scripts-gtm-id"
            value={form.gtm_id ?? ''}
            onChange={(e) => update('gtm_id', e.target.value)}
            placeholder="GTM-XXXXXX"
          />
        </Field>
        <Field
          label="GA4 measurement ID"
          htmlFor="scripts-ga4-id"
          hint='Find in Google Analytics → Admin → Data streams → Web ("G-XXXXXXXXXX").'
        >
          <UzInput
            id="scripts-ga4-id"
            value={form.ga4_id ?? ''}
            onChange={(e) => update('ga4_id', e.target.value)}
            placeholder="G-XXXXXXXXXX"
          />
        </Field>
        <Field
          label="Website Meta Pixel ID"
          htmlFor="scripts-meta-pixel-id"
          hint="Rendered on the public website. Find in Meta Events Manager → Data sources → Pixel ID (digits only)."
        >
          <UzInput
            id="scripts-meta-pixel-id"
            value={form.meta_pixel_id ?? ''}
            onChange={(e) => update('meta_pixel_id', e.target.value)}
            placeholder="1234567890123456"
          />
        </Field>
        <Field
          label="Instaform / CRM Meta Pixel ID"
          htmlFor="scripts-instaform-pixel-id"
          hint="For CRM / Instagram-form lead conversions. Reference only - it is not rendered on the website; the Meta Conversions API uses its own dataset credential."
        >
          <UzInput
            id="scripts-instaform-pixel-id"
            value={form.instaform_pixel_id ?? ''}
            onChange={(e) => update('instaform_pixel_id', e.target.value)}
            placeholder="1234567890123456"
          />
        </Field>
        <Field
          label="Clarity project ID"
          htmlFor="scripts-clarity-id"
          hint="Find in Microsoft Clarity → Settings → Project ID."
        >
          <UzInput
            id="scripts-clarity-id"
            value={form.clarity_id ?? ''}
            onChange={(e) => update('clarity_id', e.target.value)}
            placeholder="abcdef0123"
          />
        </Field>
        <Field
          label="CustomerLabs account ID"
          htmlFor="scripts-customerlabs-id"
          hint="Find in CustomerLabs → Settings → Tracking code (the id in cdn.js.customerlabs.co/<id>.js)."
        >
          <UzInput
            id="scripts-customerlabs-id"
            value={form.customerlabs_id ?? ''}
            onChange={(e) => update('customerlabs_id', e.target.value)}
            placeholder="ab12cd34ef"
          />
        </Field>
      </section>

      <section className="flex flex-col gap-4">
        <h2 className="text-sm font-semibold text-(--fg)">Server-side conversions</h2>
        <UzCheckbox
          label="Send Meta Conversions API (CAPI) events"
          description="Master switch for server-side Meta events (Lead / Qualified / PartialQualified / NonQualified / Converted). When off, the CRM sends nothing to Meta regardless of the dataset credentials. Takes effect within about a minute."
          checked={form.meta_capi_enabled}
          onCheckedChange={(c) => setForm((prev) => ({ ...prev, meta_capi_enabled: Boolean(c) }))}
          disabled={!canWrite}
        />
        <UzCheckbox
          label="Send Lead (intake) events"
          description="Only the Lead cohort (fired when a Meta lead enters the CRM). Uncheck to PAUSE Lead events while Converted / Qualified / PartialQualified / NonQualified keep firing. Leads that arrive while paused are not sent (no catch-up on resume). Needs the master switch above on. Takes effect within about a minute."
          checked={form.meta_capi_lead_enabled}
          onCheckedChange={(c) =>
            setForm((prev) => ({ ...prev, meta_capi_lead_enabled: Boolean(c) }))
          }
          disabled={!canWrite || !form.meta_capi_enabled}
        />
      </section>

      <div className="flex items-center justify-end gap-3">
        {!canWrite && (
          <span className="text-xs text-(--fg-faint)">Read-only - you can view but not save.</span>
        )}
        <UzButton
          onClick={() => void onSave()}
          disabled={!canWrite || !dirty || save.isPending}
          loading={save.isPending}
        >
          Save changes
        </UzButton>
      </div>
    </div>
  );
};
